import React, { useState } from 'react';
import ConfirmModal from '../ConfirmModal';

interface Tactic {
  id: string;
  name: string;
  createdAt?: any;
}

interface TacticsPanelProps {
  show: boolean;
  tactics: Tactic[];
  onClose: () => void;
  onLoad: (tactic: Tactic) => void;
  onDelete: (tactic: Tactic) => void;
  onSave: (name: string, overwriteId?: string) => void;
}

const TacticsPanel: React.FC<TacticsPanelProps> = ({
  show,
  tactics,
  onClose,
  onLoad,
  onDelete,
  onSave,
}) => {
  const [newName, setNewName] = useState('');
  const [confirming, setConfirming] = useState<null | Tactic>(null);
  const [pendingLoad, setPendingLoad] = useState<null | Tactic>(null);

  if (!show) return null;

  return (
    <div className='fixed top-[132px] left-0 right-0 bottom-0 z-[100] bg-white shadow-lg border-l overflow-y-auto max-w-md ml-auto'>
      <div className='p-4 border-b flex items-center justify-between'>
        <h2 className='text-lg font-bold'>Tactics Manager</h2>
        <button
          onClick={onClose}
          className='text-sm text-gray-600 hover:text-black'
        >
          Close
        </button>
      </div>

      <div className='p-4 space-y-4'>
        <div>
          <h3 className='font-semibold mb-2'>Saved Tactics</h3>
          {tactics.length === 0 ? (
            <p className='text-sm text-gray-500'>No saved tactics yet.</p>
          ) : (
            <ul className='space-y-2'>
              {tactics.map(tactic => (
                <li
                  key={tactic.id}
                  className='flex justify-between items-center border rounded px-3 py-2'
                >
                  <span className='truncate'>{tactic.name}</span>
                  <div className='flex gap-2'>
                    <button
                      onClick={() => setPendingLoad(tactic)}
                      className='text-blue-600 text-sm'
                    >
                      Load
                    </button>

                    <button
                      onClick={() => onSave(tactic.name, tactic.id)}
                      className='text-green-600 text-sm'
                    >
                      Overwrite
                    </button>

                    <button
                      onClick={() => setConfirming(tactic)}
                      className='text-red-600 text-sm'
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className='pt-4 border-t'>
          <h3 className='font-semibold mb-2'>Save New Tactic</h3>
          <input
            type='text'
            placeholder='Tactic name'
            value={newName}
            onChange={e => setNewName(e.target.value)}
            className='w-full border px-2 py-1 rounded mb-2'
          />
          <button
            onClick={() => {
              if (newName.trim()) {
                onSave(newName.trim());
                setNewName('');
              }
            }}
            className='bg-blue-700 text-white px-4 py-2 rounded w-full'
          >
            Save as New
          </button>
        </div>
      </div>

      {confirming && (
        <ConfirmModal
          title='Delete Tactic'
          message={`Are you sure you want to delete "${confirming.name}"?`}
          onConfirm={() => {
            onDelete(confirming);
            setConfirming(null);
          }}
          onCancel={() => setConfirming(null)}
        />
      )}

      {pendingLoad && (
        <ConfirmModal
          title='Load Tactic'
          message='Are you sure you want to load this tactic? Any unsaved changes will be lost.'
          onConfirm={() => {
            onLoad(pendingLoad);
            setPendingLoad(null);
          }}
          onCancel={() => setPendingLoad(null)}
        />
      )}
    </div>
  );
};

export default TacticsPanel;
